import { defineStore } from 'pinia';
import { useStoreRoot } from '~/stores/abstract/root';
import { createPopulateString, parseCollectionData } from '~/utils/strapiComponents/addStrapiData';
import { findIfPassesByGroups } from '~/utils/strapiComponents/filters';
import { useStoreSsPlayer } from '../softswiss/player';
import { strapi } from '../transport/strapi';
import type { IMainpageMenuData, IMainpageMenuStrapiResponse } from '~/types/mainpageMenu';

export const useStoreMainpageMenus = defineStore('mainpageMenus', {
  state: () => ({
    menus: [] as IMainpageMenuData[],
    requestPromise: null as Promise<IMainpageMenuStrapiResponse> | null,
  }),

  getters: {
    activeMenus(state): IMainpageMenuData[] {
      const userLang = useStoreRoot().userLang;
      const isAuthorised = useStoreSsPlayer().isPlayerAuthorised;
      return (
        state.menus
          // get races with selected language
          .filter((race) => {
            const isCorrectLang = race.locale === userLang;

            if (!isCorrectLang) {
              return false;
            }
            if (!isAuthorised) {
              return !race.isHideForGuests;
            }
            const { groupFilter } = race;
            if (!isAuthorised && groupFilter?.hideForGuests) {
              return false;
            }

            if (groupFilter) {
              const isAllowedByGroups = findIfPassesByGroups(
                groupFilter.includedGroups,
                groupFilter.excludedGroups
              );
              if (!isAllowedByGroups) {
                return false;
              }
            }
            return true;
          })
      );
    },
  },

  actions: {
    async loadMainPageMenu() {
      if (!this.requestPromise) {
        const populateString = createPopulateString(['icon', 'groupFilter']);

        this.requestPromise = strapi()
          .get('mainpage-menus', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        const parsedData = parseCollectionData(response.data);
        const filteredState = this.menus.filter(
          (item) => !parsedData.find(({ id }) => id && id === item.id)
        );

        this.menus = [...filteredState, ...parsedData].sort((a, b) => a.position - b.position);

        return true;
      } else {
        return false;
      }
    },
  },
});
